<template>
    <div class="card shadow-sm h-100 photo-tile" :class="{'photo-tile-selected': selected}">
        <div class="photo-tile-thumb" :title="selecting ? 'Vybrat' : 'Zvětšit'" @click="$emit(selecting ? 'toggle-selected' : 'open')">
            <img :src="url" :alt="photo.description" loading="lazy">
            <span v-if="isTitle" class="photo-title-badge photo-tile-badge">
                <span class="fas fa-star"></span>Titulní fotka
            </span>
            <input v-if="selecting"
                   type="checkbox"
                   class="form-check-input photo-tile-check shadow-sm"
                   tabindex="-1"
                   :checked="selected"
                   aria-label="Vybrat fotografii"
            >
            <div v-if="rotating" class="photo-tile-busy">
                <span class="spinner-border text-primary" role="status"></span>
            </div>
        </div>
        <div class="card-body p-2">
            <div v-if="canDescribe" class="photo-tile-description">
                <input type="text"
                       class="form-control form-control-sm"
                       v-model="description"
                       placeholder="Přidat popisek"
                       aria-label="Popisek fotografie"
                       @change="$emit('describe', description.trim())"
                       @keydown.enter="($event.target as HTMLInputElement).blur()"
                >
                <span class="fas fa-pencil-alt photo-tile-description-icon"></span>
            </div>
            <div class="d-flex align-items-center mt-1">
                <span class="small me-auto" :class="descriptionStateClass">{{ descriptionStateText }}</span>
                <template v-if="canRotate">
                    <button type="button" class="btn btn-sm btn-link text-secondary px-1" title="Otočit doleva" :disabled="rotating" @click="$emit('rotate', 90)">
                        <span class="fas fa-undo"></span>
                    </button>
                    <button type="button" class="btn btn-sm btn-link text-secondary px-1" title="Otočit doprava" :disabled="rotating" @click="$emit('rotate', -90)">
                        <span class="fas fa-redo"></span>
                    </button>
                </template>
                <div class="dropdown">
                    <button type="button" class="btn btn-sm btn-link text-secondary px-2" data-bs-toggle="dropdown" aria-expanded="false" title="Další akce">
                        <span class="fas fa-ellipsis-h"></span>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end shadow-sm">
                        <li>
                            <button type="button" class="dropdown-item" @click="$emit('open')">
                                <span class="fas fa-search-plus fa-fw me-2"></span>Zvětšit
                            </button>
                        </li>
                        <template v-if="canMove">
                            <li v-if="! isTitle">
                                <button type="button" class="dropdown-item" @click="$emit('make-title')">
                                    <span class="fas fa-star fa-fw me-2"></span>Nastavit jako titulní
                                </button>
                            </li>
                            <li v-if="! isTitle">
                                <button type="button" class="dropdown-item" @click="$emit('move', -1)">
                                    <span class="fas fa-arrow-left fa-fw me-2"></span>Posunout dopředu
                                </button>
                            </li>
                            <li v-if="! isLast">
                                <button type="button" class="dropdown-item" @click="$emit('move', 1)">
                                    <span class="fas fa-arrow-right fa-fw me-2"></span>Posunout dozadu
                                </button>
                            </li>
                        </template>
                        <template v-if="canRemove">
                            <li><hr class="dropdown-divider"></li>
                            <li>
                                <button type="button" class="dropdown-item text-danger" @click="$emit('remove')">
                                    <span class="fas fa-trash fa-fw me-2"></span>Smazat
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Photo} from '@/model/api';
import {computed, defineComponent, PropType, ref, watch} from 'vue';

export type DescriptionState = 'saving' | 'saved' | 'failed' | null;

export default defineComponent({
    props: {
        photo: {type: Object as PropType<Photo>, required: true},
        url: {type: String, required: true},
        isTitle: {type: Boolean, required: true},
        isLast: {type: Boolean, required: true},
        canDescribe: {type: Boolean, required: true},
        canRotate: {type: Boolean, required: true},
        canMove: {type: Boolean, required: true},
        canRemove: {type: Boolean, required: true},
        rotating: {type: Boolean, required: true},
        descriptionState: {type: String as PropType<DescriptionState>, default: null},
        selecting: {type: Boolean, required: true},
        selected: {type: Boolean, required: true},
    },

    emits: ['open', 'toggle-selected', 'describe', 'rotate', 'make-title', 'move', 'remove'],

    setup(props) {
        const description = ref(props.photo.description);

        watch(() => props.photo.description, value => {
            description.value = value;
        });

        const descriptionStateText = computed(() => (props.descriptionState === null ? '' : {
            saving: 'Ukládám…',
            saved: 'Uloženo',
            failed: 'Popisek se nepodařilo uložit',
        }[props.descriptionState]));

        const descriptionStateClass = computed(() => (props.descriptionState === 'failed' ? 'text-danger' : 'text-success'));

        return {description, descriptionStateText, descriptionStateClass};
    },
});
</script>
